import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Cpu,
  FolderGit2,
} from 'lucide-react';

import { DATA_FR } from './data/fr';
import { DATA_EN } from './data/en';
import { LABELS } from './data/labels';

import { Sidebar } from './components/Sidebar';
import { Header } from './components/Header';
import { PipelineStatus } from './components/PipelineStatus';
import { TerminalWidget } from './components/TerminalWidget';
import { CertificationsBadgesSection } from './components/CertificationsBadgesSection';
import { ExperienceSection } from './components/ExperienceSection';
import { SkillsSection } from './components/SkillsSection';
import { ProjectsSection } from './components/ProjectsSection';

export function PortfolioLayout() {
  const [activeSection, setActiveSection] = useState('overview');
  const [lang, _setLang] = useState<'fr' | 'en'>('fr');

  const data = lang === 'fr' ? DATA_FR : DATA_EN;
  const labels = LABELS[lang];

  return (
    <div className="min-h-screen bg-surface">
      <div className="max-w-7xl mx-auto p-4 md:p-8">
        <div className="flex flex-col lg:flex-row gap-6">
          <Sidebar
            activeSection={activeSection}
            setActiveSection={setActiveSection}
            data={data}
            labels={labels}
          />

          <div className="flex-1 min-w-0">
            {activeSection !== 'cv' && (
              <Header
                data={data}
                labels={labels}
              />
            )}

            {/* Mobile nav - En haut sur mobile */}
            <div className="lg:hidden mb-6 flex flex-col gap-4 no-print">
              <div className="flex justify-center gap-2 flex-wrap">
                {['overview', 'experience', 'skills', 'projects', 'badges', 'cv'].map(sectionId => {
                  let label = '';
                  switch (sectionId) {
                    case 'overview': label = labels.overview; break;
                    case 'experience': label = labels.experience; break;
                    case 'skills': label = labels.skills; break;
                    case 'projects': label = labels.projects; break;
                    case 'badges': label = labels.badges || 'Certifications'; break;
                    case 'cv': label = labels.cv; break;
                  }

                  return (
                    <button
                      key={sectionId}
                      onClick={() => setActiveSection(sectionId)}
                      className={`px-3 py-1.5 rounded-lg text-xs capitalize ${activeSection === sectionId
                        ? 'bg-[#1f75cb] text-white'
                        : 'bg-white text-[#5e5e5e]'
                        }`}
                    >
                      {label}
                    </button>
                  );
                })}
                <Link
                  to="/admin"
                  className="px-3 py-1.5 rounded-lg text-xs capitalize bg-white text-[#5e5e5e]"
                >
                  Admin
                </Link>
              </div>
            </div>

            {activeSection === 'overview' && (
              <>
                <PipelineStatus data={data} labels={labels} />
                <div className="mt-6">
                  <TerminalWidget data={data} />
                </div>
              </>
            )}

            {activeSection === 'experience' && (
              <ExperienceSection data={data} labels={labels} />
            )}

            {activeSection === 'skills' && (
              <div>
                <h2 className="section-title mb-6">
                  <Cpu className="w-5 h-5 text-[#fc6d26]" />
                  {labels.skills}
                </h2>
                <SkillsSection data={data} labels={labels} />
              </div>
            )}

            {activeSection === 'projects' && (
              <div>
                <h2 className="section-title mb-6">
                  <FolderGit2 className="w-5 h-5 text-[#1f75cb]" />
                  {labels.projects}
                </h2>
                <ProjectsSection data={data} />
              </div>
            )}

            {activeSection === 'badges' && (
              <CertificationsBadgesSection labels={labels} />
            )}

            {activeSection === 'cv' && (
              <div className="h-[calc(100vh-100px)]">
                <iframe
                  src={data.cvPdf}
                  className="w-full h-full rounded-xl border border-[#e5e5e5] bg-white"
                  title="CV"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
